import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  TableSortLabel,
  TablePagination,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import { getPageLabel } from './getPageSelectOptions';
import { getMarketCode } from '../../services/flowApiService';
import { FLAG_MAP, PLACEHOLDER_FLAG } from '../../config/marketFlags';

const sourceChipColor = (source) => {
  if (source === 'self_healed') return 'success';
  if (source === 'ui') return 'info';
  return 'default';
};

export default function LocatorsTable({
  rows,
  onEdit,
  onClone,
  page = 0,
  rowsPerPage = 50,
  total = null,
  onPageChange,
  onRowsPerPageChange,
  sortBy,
  sortOrder,
  onRequestSort,
}) {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
            <TableCell sortDirection={sortBy === 'platform' ? sortOrder : false}>
              <TableSortLabel
                active={sortBy === 'platform'}
                direction={sortBy === 'platform' ? sortOrder : 'asc'}
                onClick={(e) => onRequestSort && onRequestSort(e, 'platform')}
              >
                Platform
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortBy === 'build' ? sortOrder : false}>
              <TableSortLabel
                active={sortBy === 'build'}
                direction={sortBy === 'build' ? sortOrder : 'desc'}
                onClick={(e) => onRequestSort && onRequestSort(e, 'build')}
              >
                Build
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortBy === 'market_id' ? sortOrder : false}>
              <TableSortLabel
                active={sortBy === 'market_id'}
                direction={sortBy === 'market_id' ? sortOrder : 'asc'}
                onClick={(e) => onRequestSort && onRequestSort(e, 'market_id')}
              >
                Market
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortBy === 'page' ? sortOrder : false}>
              <TableSortLabel
                active={sortBy === 'page'}
                direction={sortBy === 'page' ? sortOrder : 'asc'}
                onClick={(e) => onRequestSort && onRequestSort(e, 'page')}
              >
                Page
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortBy === 'locator_name' ? sortOrder : false}>
              <TableSortLabel
                active={sortBy === 'locator_name'}
                direction={sortBy === 'locator_name' ? sortOrder : 'asc'}
                onClick={(e) => onRequestSort && onRequestSort(e, 'locator_name')}
              >
                Locator name
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortBy === 'locator_type' ? sortOrder : false}>
              <TableSortLabel
                active={sortBy === 'locator_type'}
                direction={sortBy === 'locator_type' ? sortOrder : 'asc'}
                onClick={(e) => onRequestSort && onRequestSort(e, 'locator_type')}
              >
                Type
              </TableSortLabel>
            </TableCell>
            <TableCell
              sx={{ minWidth: 300 }}
              sortDirection={sortBy === 'locator_value' ? sortOrder : false}
            >
              <TableSortLabel
                active={sortBy === 'locator_value'}
                direction={sortBy === 'locator_value' ? sortOrder : 'asc'}
                onClick={(e) => onRequestSort && onRequestSort(e, 'locator_value')}
              >
                Value
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortBy === 'source' ? sortOrder : false}>
              <TableSortLabel
                active={sortBy === 'source'}
                direction={sortBy === 'source' ? sortOrder : 'asc'}
                onClick={(e) => onRequestSort && onRequestSort(e, 'source')}
              >
                Source
              </TableSortLabel>
            </TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const marketCode = getMarketCode(row.market_id);
            const marketDisplay = marketCode === 'playground' ? 'core' : marketCode || 'unknown';
            return (
              <TableRow key={row.id} hover>
                <TableCell>{row.platform}</TableCell>
                <TableCell>
                  {row.build} {row.build === 0 && '(base)'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <img
                      src={FLAG_MAP[row.market_id] || PLACEHOLDER_FLAG}
                      alt={`${marketDisplay} flag`}
                      style={{ width: 30, height: 30, marginRight: 8, mixBlendMode: 'multiply' }}
                    />
                    {marketDisplay}
                  </Box>
                </TableCell>
                <TableCell>{getPageLabel(row.page)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  {row.locator_name}
                </TableCell>
                <TableCell>{row.locator_type}</TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: '0.72rem', wordBreak: 'break-all' }}
                  >
                    {row.locator_value}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.source}
                    size="small"
                    color={sourceChipColor(row.source)}
                    variant="outlined"
                    sx={{ fontSize: 10 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Edit type & value">
                      <IconButton size="small" onClick={() => onEdit(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Clone for new build">
                      <IconButton size="small" onClick={() => onClone(row)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {total != null ? (
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
          <Typography variant="body2">
            Showing {page * rowsPerPage + 1} - {page * rowsPerPage + rows.length}
          </Typography>
          <Box>
            <IconButton
              size="small"
              onClick={(e) => onPageChange && onPageChange(e, Math.max(0, page - 1))}
              disabled={page === 0}
            >
              {'<'}
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => onPageChange && onPageChange(e, page + 1)}
              disabled={rows.length < rowsPerPage}
            >
              {'>'}
            </IconButton>
          </Box>
        </Box>
      )}
    </TableContainer>
  );
}
